/** Client-side auth session helpers (JWT in localStorage). */

import { clearBootstrapCache } from "@/lib/bootstrap-cache";
import { getCanonicalOrigin, isCanonicalHost, isOperatorPath, isPublicEventHost } from "@/lib/canonical-host";
import { resetLoginDocumentBranding } from "@/lib/tenant-brand";

export const AUTH_SESSION_KEY = "aivoryems.auth.session";

const CLOCK_SKEW_MS = 60 * 1000;

export function decodeJwtPayload(token) {
  try {
    const segment = String(token || "").split(".")[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  return Number.isFinite(exp) ? exp * 1000 : null;
}

export function getAuthSession() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveAuthSession(session) {
  if (typeof window === "undefined" || !session?.token) return;
  const expMs = getTokenExpiryMs(session.token);
  localStorage.setItem(
    AUTH_SESSION_KEY,
    JSON.stringify({
      ...session,
      at: session.at || new Date().toISOString(),
      exp: expMs ? new Date(expMs).toISOString() : session.exp,
    }),
  );
}

export function clearAuthSession() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(AUTH_SESSION_KEY);
}

const UI_THEME_KEY = "aivoryems.ui.theme";

/**
 * Sign out from admin or platform: clear session, strip whitelabel document CSS,
 * and hard-navigate to login so the page is not left with stale SPA state.
 */
export function performSignOut(loginPath = "/login") {
  if (typeof window === "undefined") return;
  clearAuthSession();
  clearBootstrapCache();
  resetLoginDocumentBranding();
  try {
    localStorage.setItem(UI_THEME_KEY, "light");
  } catch {
    // no-op
  }
  const origin = isCanonicalHost() ? "" : getCanonicalOrigin();
  window.location.assign(`${origin}${loginPath}`);
}

export function isTokenExpired(token, skewMs = CLOCK_SKEW_MS) {
  const expMs = getTokenExpiryMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - skewMs;
}

export function isAuthSessionValid() {
  const session = getAuthSession();
  if (!session?.email || !session?.token) return false;
  return !isTokenExpired(session.token);
}

export function redirectToLogin(reason = "expired") {
  if (typeof window === "undefined") return;

  clearAuthSession();
  clearBootstrapCache();
  resetLoginDocumentBranding();
  // White-label event domains are attendee-only — never send visitors to /login.
  if (isPublicEventHost()) return;

  const params = new URLSearchParams();
  if (reason) params.set("session", reason);

  const pathname = window.location.pathname || "";
  let returnPath = "/admin";
  if (isCanonicalHost() && isOperatorPath(pathname)) {
    returnPath = pathname.split("?")[0] || "/admin";
  }
  if (returnPath && !returnPath.startsWith("/login")) {
    params.set("return", returnPath);
  }

  const query = params.toString();
  const loginUrl = query ? `/login?${query}` : "/login";
  const origin = isCanonicalHost() ? "" : getCanonicalOrigin();
  window.location.assign(`${origin}${loginUrl}`);
}
